"use client";

import { useState } from "react";
import type { ReactNode } from "react";

type AccordionItemProps = {
  trigger: ReactNode;
  children: ReactNode;
  cardClassName?: string;
  buttonClassName?: string;
  contentClassName?: string;
  chevronClassName?: string;
  defaultOpen?: boolean;
};

/**
 * Reusable accordion row so marketing sections can reveal details without leaving the page.
 *
 * @param trigger - The visible heading or summary that users click.
 * @param children - The expandable content shown when the row is open.
 * @param cardClassName - Optional wrapper styling for the card container.
 * @param buttonClassName - Optional styling for the toggle button.
 * @param contentClassName - Optional styling for the collapsible content area.
 * @param chevronClassName - Optional styling for the expand/collapse icon.
 * @param defaultOpen - Controls whether the accordion starts expanded.
 * @returns A client-side accordion item with open/closed state.
 */
export default function AccordionItem({
  trigger,
  children,
  cardClassName = "",
  buttonClassName = "w-full flex items-center justify-between text-left focus:outline-none",
  contentClassName = "",
  chevronClassName = "material-symbols-outlined transition-transform",
  defaultOpen = false,
}: AccordionItemProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={cardClassName}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className={buttonClassName}
      >
        {/* Fragment-wrap the caller-supplied trigger so it stays a single child
            next to the chevron sibling below. */}
        <>{trigger}</>
        <span className={`${chevronClassName} ${open ? "rotate-180" : ""}`}>
          expand_more
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className={`overflow-hidden ${contentClassName}`}>{children}</div>
      </div>
    </div>
  );
}
